import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useAppNavigation } from "@router/use-app-navigation";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { updatePlan } from "@store/plans/plans-thunks";
import { useCallback } from "react";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { CompanyProfileTab, ProjectProfileTab } from "@router/route-params";
import { plansCreationStateSelector } from "@store/plan-creation/plan-creation-slice";
import {
  copiedPlanSceneSeatsSelector,
  isEndingCopiedPlanSelector,
  sceneSeatsToCreateSelector,
} from "@store/plan-creation/plan-creation-selectors";
import {
  createSceneSeatsForPlan,
  sceneSeatsCreationFailedMarkup,
  sceneSeatsTransferFailedMarkup,
  SCENE_SEATS_CREATION_FAILED_TITLE,
  SCENE_SEATS_TRANSFER_FAILED_TITLE,
  transferSceneSeatAssignments,
} from "@utils/scene-seat-utils";
import {
  createTokenPlansForPlan,
  tokenPlansCreationFailedMarkup,
  TOKEN_PLANS_CREATION_FAILED_TITLE,
} from "@utils/token-plan-utils";
import { isActiveUsersLimitType } from "@utils/limit-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";
import { shouldTransferSceneSeatsNow } from "@utils/plan-copy-utils";

interface ReturnProps {
  /**
   * Create a plan, together with the SCENE seats and the token plans that come with it.
   * Both are only created after the plan itself exists, as they are attached to it.
   */
  createPlan: (plan: FaroCoreApiTypes.CreatePlanPayload) => Promise<void>;
}

/** Hook to create a plan */
export function useCreatePlan(): ReturnProps {
  const dispatch = useAppDispatch();
  const { navigateToProfilePage } = useAppNavigation();
  const { handleErrorWithDialog } = useErrorHandler();
  const { trackEvent } = useTrackEvent();

  const {
    allLimits,
    tokensPerYear,
    copiedPlan,
    creationDates: { durationType },
    planSubject: { planSubjectId, planSubjectType },
  } = useAppSelector(plansCreationStateSelector);
  const sceneSeatsToCreate = useAppSelector(sceneSeatsToCreateSelector);
  const copiedPlanSceneSeats = useAppSelector(copiedPlanSceneSeatsSelector);
  const shouldEndCopiedPlan = useAppSelector(isEndingCopiedPlanSelector);

  /**
   * Puts the copied plan back to the end date it had before it was ended for its copy.
   * If that is not possible either, the admin has to learn that the plan has to be fixed manually.
   */
  const restoreCopiedPlanEndDate = useCallback(
    async (planToRestore: FaroCoreApiTypes.AdmPlan): Promise<void> => {
      if (!planToRestore.endDate) {
        return;
      }

      try {
        await dispatch(
          updatePlan({
            planId: planToRestore.id,
            updatedProps: {
              type: "updatingEndDate",
              endDate: planToRestore.endDate,
            },
          })
        ).unwrap();
      } catch (error) {
        handleErrorWithDialog({
          id: `restore-copied-plan-${Date.now().toString()}`,
          title: `The copy could not be created, and the copied plan could not be put back to its end date ${planToRestore.endDate}. Please update the plan manually.`,
          error,
        });
      }
    },
    [dispatch, handleErrorWithDialog]
  );

  const createPlan = useCallback(
    async (plan: FaroCoreApiTypes.CreatePlanPayload) => {
      if (planSubjectType && planSubjectId) {
        try {
          // The copied plan is ended first, so that the copy which starts right then does not
          // overlap with it. It is put back to its original end date if the copy fails.
          // It ends when the copy starts, which is few seconds ahead of now (see getEndingNowDate),
          // so that the end date has not passed yet when the request reaches the backend.
          if (shouldEndCopiedPlan && copiedPlan) {
            try {
              await dispatch(
                updatePlan({
                  planId: copiedPlan.plan.id,
                  updatedProps: {
                    type: "updatingEndDate",
                    endDate: plan.startDate,
                  },
                })
              ).unwrap();
            } catch (error) {
              handleErrorWithDialog({
                id: `end-copied-plan-${Date.now().toString()}`,
                title:
                  "The copied plan could not be ended, so no copy was created.",
                error,
              });
              return;
            }
          }

          let createdPlan: FaroCoreApiTypes.AdmPlan;
          try {
            createdPlan = await faroCoreApiClient.admin.createPlan(plan);
          } catch (error) {
            if (shouldEndCopiedPlan && copiedPlan) {
              await restoreCopiedPlanEndDate(copiedPlan.plan);
            }
            throw error;
          }

          let createdSceneSeats: FaroCoreApiTypes.AdmPlan[] = [];
          if (sceneSeatsToCreate > 0) {
            const result = await createSceneSeatsForPlan({
              plan: createdPlan,
              seatCount: sceneSeatsToCreate,
            });
            createdSceneSeats = result.createdSeats;

            // The plan itself was created, so the seats that are missing have to be
            // added manually. Both the admin and the team have to learn about that.
            if (result.failedCount > 0) {
              trackEvent({
                name: PlanEvents.createSceneSeatsFailed,
                props: {
                  planId: createdPlan.id,
                  requestedSceneSeats: result.requestedCount,
                  failedSceneSeats: result.failedCount,
                },
              });

              handleErrorWithDialog({
                id: `create-scene-seats-${Date.now().toString()}`,
                title: SCENE_SEATS_CREATION_FAILED_TITLE,
                error: sceneSeatsCreationFailedMarkup(result),
              });
            }
          }

          // The members holding a seat of the copied plan move over to the seats of the copy, but
          // only once the copied plan stops granting SCENE access. A copy that starts in the future
          // leaves the current seats untouched, so that nobody loses access before the switchover.
          if (
            copiedPlan &&
            copiedPlanSceneSeats.length > 0 &&
            shouldTransferSceneSeatsNow({
              copiedPlan: copiedPlan.plan,
              shouldEndCopiedPlan,
              copyStartDate: Date.parse(plan.startDate),
            })
          ) {
            const result = await transferSceneSeatAssignments({
              copiedPlan: copiedPlan.plan,
              oldSeats: copiedPlanSceneSeats,
              newSeats: createdSceneSeats,
            });

            if (result.failedCount > 0) {
              trackEvent({
                name: PlanEvents.copySceneSeatAssignmentsFailed,
                props: {
                  planId: createdPlan.id,
                  failedRequests: result.failedCount,
                },
              });

              handleErrorWithDialog({
                id: `copy-scene-seats-${Date.now().toString()}`,
                title: SCENE_SEATS_TRANSFER_FAILED_TITLE,
                error: sceneSeatsTransferFailedMarkup(result),
              });
            }
          }

          if (tokensPerYear > 0) {
            const result = await createTokenPlansForPlan({
              plan: createdPlan,
              tokensPerYear,
              isUnlimited: durationType === "unlimited",
            });

            // The plan itself was created, so the years that are missing have to be added
            // manually. Both the admin and the team have to learn about that.
            if (result.failedCount > 0) {
              trackEvent({
                name: PlanEvents.createTokenPlansFailed,
                props: {
                  planId: createdPlan.id,
                  requestedTokenPlans: result.requestedCount,
                  failedTokenPlans: result.failedCount,
                },
              });

              handleErrorWithDialog({
                id: `create-token-plans-${Date.now().toString()}`,
                title: TOKEN_PLANS_CREATION_FAILED_TITLE,
                error: tokenPlansCreationFailedMarkup(result),
              });
            }
          }
        } catch (error) {
          const typedError = error as { devMessage?: string };

          // Determine if the error message indicates a conflict with an existing active users limit
          const hasActiveUsersLimit = allLimits.some((limit) =>
            isActiveUsersLimitType(limit.type)
          );
          if (
            hasActiveUsersLimit &&
            typedError?.devMessage?.includes("conflicts")
          ) {
            handleErrorWithDialog({
              id: `use-create-plan-${Date.now().toString()}`,
              title:
                "Please deactivate the existing limit before adding a new one.",
              error,
            });
          } else {
            handleErrorWithDialog({
              id: `use-create-plan-${Date.now().toString()}`,
              title:
                "An error occurred while creating a new plan. Please try again later.",
              error,
            });
          }
        } finally {
          if (planSubjectType === "COMPANY" || planSubjectType === "DEVICE") {
            navigateToProfilePage({
              subjectType: planSubjectType,
              subjectId: planSubjectId,
              tab: CompanyProfileTab.plans,
            });
          } else if (planSubjectType === "PROJECT") {
            navigateToProfilePage({
              subjectType: planSubjectType,
              subjectId: planSubjectId,
              tab: ProjectProfileTab.plans,
            });
          }
        }
      }
    },
    [
      allLimits,
      copiedPlan,
      copiedPlanSceneSeats,
      dispatch,
      durationType,
      handleErrorWithDialog,
      navigateToProfilePage,
      planSubjectId,
      planSubjectType,
      restoreCopiedPlanEndDate,
      sceneSeatsToCreate,
      shouldEndCopiedPlan,
      tokensPerYear,
      trackEvent,
    ]
  );

  return { createPlan };
}
